import React, { useState, useEffect, useContext, useCallback } from 'react';
import ReactModal from 'react-modal';
import { BoxModal } from '../../boxModal';
import { CiCircleInfo } from 'react-icons/ci';
import './cartao-RequestCard-modal.css';
import { getAddressByCep } from '../../../utils/getAddressByCep';
import { AuthContext } from '../../../contexts/AuthContext';
import { birthState } from '../../../utils/birthState';
import { toast } from 'react-toastify';
import { InputField } from './inputField';
import { useCards } from '../../../hooks/useCards';

const CEP_CACHE = new Map();

const buildInitialForm = (cardType) => ({
  address: '',
  addressNumber: '',
  addressComplement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  cardType,
  isDependent: 'false',
  dependentName: '',
  isSecondCopy: false,
});

export const ModalRequestCard = ({ isOpen, onClose, cardType, isFirstCard = false }) => {
  const { user } = useContext(AuthContext);
  const { requestCard } = useCards();
  const [formData, setFormData] = useState(buildInitialForm(cardType));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCepValid, setIsCepValid] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);

  // resolveCep sem deps de estado — usa apenas setters estáveis e CEP_CACHE de módulo
  const resolveCep = useCallback(async (rawCep) => {
    const cep = (rawCep || '').replace(/[^0-9]/g, '');
    if (cep.length !== 8) {
      setIsCepValid(false);
      return;
    }
    setIsCepLoading(true);
    setError('');
    try {
      let data = CEP_CACHE.get(cep);
      if (!data) {
        const result = await getAddressByCep(cep);
        if (!result || result.erro) {
          setError('CEP não encontrado.');
          setIsCepValid(false);
          return;
        }
        CEP_CACHE.set(cep, result);
        data = result;
      }
      const stateIndex = birthState.findIndex((s) => s.label === data.uf);
      setFormData((prev) => ({
        ...prev,
        address: data.logradouro || prev.address,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: stateIndex !== -1 ? birthState[stateIndex].label : prev.state,
      }));
      setIsCepValid(true);
    } catch {
      setError('Erro ao buscar o CEP.');
      setIsCepValid(false);
    } finally {
      setIsCepLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Preenche endereço do perfil ao abrir; reseta ao fechar
  useEffect(() => {
    if (!isOpen) {
      setFormData(buildInitialForm(cardType));
      setError('');
      setIsCepValid(false);
      setIsCepLoading(false);
      return;
    }
    // Cartão adicional só pode ser para dependente
    if (!isFirstCard) {
      setFormData((prev) => ({ ...prev, isDependent: 'true' }));
    }
    if (!user?.address?.length) {
      setError('Endereço não encontrado no perfil. Preencha o CEP manualmente.');
      return;
    }
    const addr = user.address[0];
    const cep = (addr.zipCode || '').replace(/\D/g, '');
    setFormData((prev) => ({
      ...prev,
      zipCode: cep,
      address: addr.addressLine || '',
      addressNumber: addr.addressLine2 || '',
      addressComplement: addr.complement || '',
      neighborhood: addr.neighborhood || '',
      city: addr.cityName || '',
      state: addr.state || '',
    }));
    if (cep.length === 8) resolveCep(cep);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (field, value) => {
    if (error) setError('');
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCepChange = async (e) => {
    const cepValue = e.target.value.replace(/[^0-9]/g, '');
    setFormData((prev) => ({ ...prev, zipCode: cepValue }));
    setIsCepValid(false);
    if (error) setError('');
    if (cepValue.length === 8) await resolveCep(cepValue);
  };

  const handleSubmit = async () => {
    if (!isCepValid) {
      setError('Informe um CEP válido para continuar.');
      return;
    }
    if (loading) return;
    try {
      setLoading(true);
      await requestCard({
        ...formData,
        isDependent: formData.isDependent === 'true',
      });
      toast.success('Cartão solicitado com sucesso!');
      onClose();
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao solicitar cartão');
    } finally {
      setLoading(false);
    }
  };

  const isFormDisabled = isCepLoading || !isCepValid;
  const isDependent = formData.isDependent === 'true';

  return (
    <ReactModal
      isOpen={isOpen}
      className="change-modal"
      overlayClassName="overlay-change-password-modal"
    >
      <BoxModal maxWidth={500} title="Solicitar Cartão" close={onClose}>
        <div className="request-card-form">
          <p className="request-card-subtitle">Confirme o endereço de entrega do seu cartão.</p>

          <div className="d-flex flex-column gap-2">
            <InputField
              label="CEP*"
              value={formData.zipCode}
              maxLength={8}
              placeholder="Ex: 12345678"
              onChange={handleCepChange}
              disabled={false}
            />

            {isCepLoading && (
              <span className="request-card-cep-loading">Buscando endereço...</span>
            )}

            <InputField
              label="Logradouro*"
              value={formData.address}
              disabled={isFormDisabled}
              onChange={(e) => handleInputChange('address', e.target.value)}
            />

            <div className="request-card-row">
              <InputField
                label="Número*"
                value={formData.addressNumber}
                disabled={isFormDisabled}
                onChange={(e) => handleInputChange('addressNumber', e.target.value)}
              />
              <InputField
                label="Complemento"
                value={formData.addressComplement}
                disabled={isFormDisabled}
                onChange={(e) => handleInputChange('addressComplement', e.target.value)}
              />
            </div>

            <div className="request-card-row">
              <InputField
                label="Bairro*"
                value={formData.neighborhood}
                disabled={isFormDisabled}
                onChange={(e) => handleInputChange('neighborhood', e.target.value)}
              />
              <InputField
                label="Cidade*"
                value={formData.city}
                disabled={isFormDisabled}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </div>

            <label className="request-card-field-label">
              Estado*
              <select
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                disabled={isFormDisabled}
              >
                <option value="">Selecione o Estado</option>
                {birthState.map((state) => (
                  <option key={state.label} value={state.label}>{state.label}</option>
                ))}
              </select>
            </label>

            {!isFirstCard && (
              <label className="request-card-field-label">
                Solicitar para
                <select
                  value={formData.isDependent}
                  onChange={(e) => {
                    handleInputChange('isDependent', e.target.value);
                    if (e.target.value === 'false') handleInputChange('dependentName', '');
                  }}
                >
                  <option value="true">Dependente</option>
                </select>
              </label>
            )}

            {(!isFirstCard && isDependent) && (
              <InputField
                label="Nome do Dependente*"
                value={formData.dependentName}
                onChange={(e) => handleInputChange('dependentName', e.target.value)}
                className={error && !formData.dependentName ? 'error' : ''}
              />
            )}

            {error && (
              <div className="error-message">
                <CiCircleInfo className="error-icon" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="modal-buttons-requestCard">
            <button className="request-card-btn request-card-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="request-card-btn request-card-btn-primary"
              onClick={handleSubmit}
              disabled={loading || isCepLoading}
            >
              {loading ? 'Solicitando...' : 'Solicitar Cartão'}
            </button>
          </div>
        </div>
      </BoxModal>
    </ReactModal>
  );
};
